import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { FEATURE_ACCOMPAGNEMENT, FEATURE_CORRESPONDANCE } from "../config/features";

/**
 * FAQ enrichie SEO — questions groupées par intention de recherche.
 * IMPORTANT: les 6 questions originales sont conservées dans leur formulation exacte.
 * Balisage FAQPage JSON-LD injecté dynamiquement (aligné 1-1 avec le contenu visible).
 */

/** Strip trailing "un accompagnement peut vous aider" hints when the feature is off. */
function stripAccompagnementHints(text) {
  return text
    .replace(/\s*Si vous êtes bloqué[^.]*\./gi, "")
    .replace(/\s*Un accompagnement[^.]*\./gi, "")
    .replace(/\s*Si vous cherchez[^.]*je vous accompagne\./gi, "")
    .replace(/\s*Je peux vous accompagner[^.]*\./gi, "")
    .replace(/\s*un accompagnement peut vous aider[^.]*\./gi, "")
    .trim();
}

const sections = [
  {
    key: "programme",
    title: "Le programme d'écriture",
    items: [
      { q: "À quel rythme reçoit-on les emails du programme ?",
        a: "Un email par semaine, à jour fixe, pendant 52 semaines." },
      { q: "Puis-je faire une pause ?",
        a: "Le programme est envoyé automatiquement. Tu n'es obligée d'ouvrir aucun email — les thèmes restent disponibles dans ta boîte." },
      { q: "Faut-il savoir écrire pour participer au programme ?",
        a: "Non. Le programme s'adresse à celles et ceux qui ont envie d'écrire, sans prétention. Il n'y a aucun niveau requis — juste l'envie de venir au rendez-vous, quand on peut, comme on peut." },
      { q: "Comment se déroule un rendez-vous d'écriture ?",
        a: "Chaque semaine, tu reçois par email un thème inspirant, un extrait littéraire et une invitation à écrire à ton tour. Le rendez-vous est libre : quelques minutes ou une heure, un carnet ou une application, tu choisis." },
    ],
  },
  {
    key: "correspondance",
    title: "La correspondance manuscrite",
    items: [
      { q: "Combien de temps pour recevoir ta réponse ?",
        a: "Je réponds sous 2 semaines après réception de ta lettre. Chaque lettre est écrite à la main." },
      { q: "Peut-on m'écrire depuis l'étranger ?",
        a: "Oui, la correspondance est disponible partout dans le monde au tarif unique." },
      { q: "Puis-je offrir le programme ou une correspondance ?",
        a: "Absolument. Contacte-moi et je m'occupe de tout." },
      { q: "Comment fonctionne l'abonnement mensuel à la correspondance ?",
        a: "Chaque mois, tu reçois un email avec un nouveau code personnel et mon adresse postale. Tu m'écris quand tu veux, tu reportes le code sur ton enveloppe, et je te réponds à la main sous deux semaines. Engagement de 3 mois minimum, résiliable ensuite à tout moment." },
    ],
  },
  {
    key: "accompagnement",
    title: "L'accompagnement d'écriture",
    items: [
      { q: "Qu'est-ce qu'un accompagnement à l'écriture ?",
        a: "C'est un temps d'échange où je vous aide à mettre en mots ce que vous portez en vous — un discours, un hommage, une lettre importante. Ensemble, nous transformons vos souvenirs, vos émotions et vos idées en un texte qui vous ressemble vraiment." },
      { q: "À qui s'adresse l'accompagnement à l'écriture ?",
        a: "À toute personne qui a quelque chose d'important à dire mais ne trouve pas les mots. Que vous soyez témoin de mariage, que vous ayez à rendre hommage à un proche, ou que vous vouliez écrire une lettre qui compte — l'accompagnement s'adapte à votre besoin." },
      { q: "Peut-on être accompagné sans savoir écrire ?",
        a: "Oui, absolument. Vous n'avez pas besoin d'être écrivain. Vous avez vos souvenirs, vos émotions, vos anecdotes — c'est tout ce qui compte. Mon rôle est de vous aider à les mettre en forme, pas de vous demander de savoir déjà écrire." },
      { q: "Quelle différence entre accompagnement à l'écriture et rédaction complète ?",
        a: "L'accompagnement (110€) vous aide à structurer votre texte : vous repartez avec une trame que vous finalisez vous-même, avec vos mots. La rédaction complète (180€) inclut la rédaction entière du texte par mes soins, à partir de nos échanges — je vous remets un support imprimé prêt à être lu." },
      { q: "Comment se déroule la rédaction d'un discours ?",
        a: "Nous commençons par un échange (visio, téléphone ou en personne à Metz) où vous me racontez : la personne, les moments, ce que vous voulez transmettre. Je prends note, je structure, j'écris. Selon la formule choisie, je vous remets soit une trame à personnaliser, soit un texte entièrement rédigé." },
      { q: "Combien coûte la rédaction d'un discours ?",
        a: "L'accompagnement d'écriture (structure + relecture) est à 110€. La rédaction complète (texte entier écrit par mes soins, prêt à lire) est à 180€. Un premier échange téléphonique est offert pour parler de votre projet." },
      { q: "Combien de temps faut-il pour préparer un discours ?",
        a: "Comptez idéalement 2 à 3 semaines avant l'événement. Cela permet un premier échange, la rédaction, une relecture ensemble et éventuellement un ajustement. Pour les demandes urgentes, contactez-moi : je fais au mieux selon mon planning." },
    ],
  },
  {
    key: "mariage",
    title: "Rédaction de discours de mariage",
    items: [
      { q: "Comment écrire un discours de mariage ?",
        a: "Commencez par lister vos souvenirs les plus vrais, pas les plus grandioses. Une anecdote sincère touche plus qu'une belle phrase creuse. Structurez ensuite en trois temps : d'où vient votre lien avec la personne, ce que vous voulez lui souhaiter, un mot pour son ou sa partenaire. Si vous êtes bloqué, je peux vous accompagner." },
      { q: "Comment écrire un discours quand on est témoin ?",
        a: "Le discours de témoin repose sur la relation d'amitié. Racontez comment vous vous êtes rencontrés, ce que cette amitié vous apporte, comment vous avez vu naître leur amour. L'émotion vient toujours du détail. Un accompagnement peut vous aider à choisir les bons moments à raconter." },
      { q: "Comment rendre un discours de mariage émouvant ?",
        a: "L'émotion se cache dans le concret : un geste, une phrase entendue, un souvenir précis. Plutôt que \"tu es généreux\", dites \"je me souviens du soir où tu as…\". Parlez au \"tu\" plutôt qu'à l'auditoire. Terminez sur une image, pas sur un conseil." },
    ],
  },
  {
    key: "hommage",
    title: "Rédaction d'hommage et d'éloge funèbre",
    items: [
      { q: "Comment écrire un hommage à un proche ?",
        a: "Écrivez avec le cœur, pas avec la tête. Ce qui touche, ce sont les détails vrais : sa voix, son rire, une habitude, une phrase qu'il ou elle disait. N'essayez pas de tout dire — un seul souvenir bien raconté vaut mieux qu'une liste. Si vous cherchez de l'aide pour trouver les mots, je vous accompagne." },
      { q: "Comment rédiger un éloge funèbre ?",
        a: "Un éloge funèbre honore la vie d'une personne : ce qui la rendait unique, les liens qu'elle tissait, l'empreinte qu'elle laisse. Structurez en trois temps : qui elle était, ce qu'elle nous a donné, comment nous continuerons de la porter. Restez vrai — l'authenticité prime toujours." },
      { q: "Comment trouver les mots après un décès ?",
        a: "Il n'y a pas de bons mots — seulement les vôtres. Prenez un carnet et écrivez sans filtre ce qui vous vient : ce qui vous manque, ce que vous auriez voulu dire, ce que vous gardez. Ces notes brutes deviennent souvent la matière première du plus beau des textes. Je peux vous accompagner dans ce chemin." },
      { q: "Peut-on faire rédiger un hommage à partir de souvenirs personnels ?",
        a: "Oui. C'est exactement le principe de la rédaction complète (180€) : vous me confiez vos souvenirs, vos émotions, ce que la personne représentait pour vous. J'en fais un texte qui vous ressemble et qui rend hommage avec justesse. Vous le lisez, ou nous le retravaillons ensemble." },
    ],
  },
  {
    key: "lettres",
    title: "Écrire une lettre ou un texte personnel",
    items: [
      { q: "Comment écrire une lettre personnelle ?",
        a: "Écrivez comme vous parleriez à cette personne, à voix basse. Une lettre personnelle n'a pas besoin d'être longue ni parfaite : elle a besoin d'être vraie. Commencez par ce qui vous vient en premier, même si c'est un détail minuscule. Le reste suivra." },
      { q: "Comment trouver les mots pour une lettre importante ?",
        a: "Souvent, les mots viennent quand on cesse de les chercher. Écrivez un premier jet sans vous corriger — juste pour vider ce que vous portez. Puis relisez, gardez ce qui sonne juste, coupez le reste. Si vous vous sentez bloqué, un accompagnement peut vous aider à dénouer." },
    ],
  },
  {
    key: "paiement",
    title: "Paiement et pratique",
    items: [
      { q: "Le paiement est-il sécurisé ?",
        a: "Oui, tous les paiements passent par Stripe. Aucune donnée bancaire ne transite par notre serveur." },
      { q: "Peut-on utiliser un code promo ou une carte cadeau sur le site ?",
        a: "Oui. Sur les pages Programmes et Correspondance, un champ permet de saisir ton code — qu'il s'agisse d'une carte cadeau ou d'un code promo. La réduction s'applique automatiquement." },
    ],
  },
];

// Flat list for the JSON-LD schema (Google FAQ rich snippet).
// When the accompagnement feature is off, the whole section is excluded AND
// any trailing "je vous accompagne" hints inside other sections are stripped.
const visibleSections = sections
  .filter(s => (FEATURE_ACCOMPAGNEMENT || s.key !== "accompagnement") && (FEATURE_CORRESPONDANCE || s.key !== "correspondance"))
  .map(s => ({
    ...s,
    items: s.items.map(it => (
      FEATURE_ACCOMPAGNEMENT ? it : { ...it, a: stripAccompagnementHints(it.a) }
    )),
  }));
const flatQuestions = visibleSections.flatMap(s => s.items);

export default function FAQ() {
  useEffect(() => {
    document.title = FEATURE_ACCOMPAGNEMENT
      ? "FAQ — Écriture, correspondance, discours et hommage | Les RDV de l'écriture"
      : "FAQ — Programmes d'écriture & correspondance manuscrite | Les RDV de l'écriture";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content",
      FEATURE_ACCOMPAGNEMENT
        ? "Questions fréquentes sur les programmes d'écriture, la correspondance manuscrite et l'accompagnement d'écriture pour discours de mariage, hommages et lettres personnelles."
        : "Questions fréquentes sur les programmes d'écriture et la correspondance manuscrite des Rendez-vous de l'écriture.");

    // Inject FAQPage schema
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-jsonld";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": flatQuestions.map(it => ({
        "@type": "Question",
        "name": it.q,
        "acceptedAnswer": { "@type": "Answer", "text": it.a },
      })),
    });
    document.head.appendChild(script);
    return () => { document.getElementById("faq-jsonld")?.remove(); };
  }, []);

  return (
    <div data-testid="faq-page" className="max-w-3xl mx-auto px-6 md:px-12 py-20">
      <p className="font-hand text-2xl text-[#A3876A] mb-4">Questions</p>
      <h1 className="font-serif text-5xl md:text-6xl mb-12">Foire aux questions</h1>

      {visibleSections.map((sec) => (
        <section key={sec.key} className="mb-12" data-testid={`faq-section-${sec.key}`}>
          <h2 className="font-serif text-2xl text-[#2C2621] mb-4">{sec.title}</h2>
          <Accordion type="single" collapsible className="space-y-2">
            {sec.items.map((it, i) => (
              <AccordionItem key={i} value={`${sec.key}-q${i}`} className="border-b border-[#E8DCC8]">
                <AccordionTrigger data-testid={`faq-q-${sec.key}-${i}`} className="text-left font-serif text-lg text-[#2C2621] py-4">{it.q}</AccordionTrigger>
                <AccordionContent className="text-[#4A4036] leading-relaxed pb-5">{it.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      ))}

      <div className="mt-16 pt-10 border-t border-[#E8DCC8] text-center">
        <p className="font-hand text-2xl text-[#A3876A] mb-3">Une autre question ?</p>
        <p className="text-[#4A4036] mb-6">
          Écris-moi, je te réponds dans les 48 heures.
        </p>
        <Link to="/contact" className="btn-primary">Me contacter</Link>
      </div>
    </div>
  );
}
